import {
  useDeleteMaintenanceImage,
  useGetMaintenanceImage,
  useUploadMaintenanceImage,
} from "@/api/cms";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { Loader2, Trash2, Upload } from "lucide-react";
import { useRef } from "react";

/** Zone d'aperçu de l'image de maintenance (paysage, centrée). */
const IMAGE_BOX_CLASS =
  "relative h-40 w-full max-w-md overflow-hidden rounded-xl border border-slate-300 bg-white";

const CmsMaintenanceImageUpload = () => {
  const cmsNew = useCmsNew();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useGetMaintenanceImage();
  const { mutate: uploadImage, isPending: isUploading } =
    useUploadMaintenanceImage();
  const { mutate: deleteImage, isPending: isDeleting } =
    useDeleteMaintenanceImage();

  const isProcessing = isUploading || isDeleting;
  const hasImage = Boolean(data?.maintenanceImage);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadImage(file, {
      onError: () => {
        toast({
          title: cmsNew(
            "cms.new.maintenance.imageUploadError",
            "Impossible d'envoyer l'image",
          ),
          duration: 3000,
          variant: "error",
        });
      },
    });
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    deleteImage(undefined, {
      onError: () => {
        toast({
          title: cmsNew(
            "cms.new.maintenance.imageDeleteError",
            "Impossible de supprimer l'image",
          ),
          duration: 3000,
          variant: "error",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div
        className={cn(
          IMAGE_BOX_CLASS,
          "flex items-center justify-center bg-slate-50",
        )}
      >
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (hasImage && data?.maintenanceImageUrl) {
    return (
      <div className={IMAGE_BOX_CLASS}>
        <img
          key={data.maintenanceImageUrl}
          src={data.maintenanceImageUrl}
          alt={cmsNew("cms.new.maintenance.imageAlt", "Image de maintenance")}
          className="size-full object-contain p-3"
        />
        <button
          type="button"
          onClick={handleDelete}
          disabled={isProcessing}
          title={cmsNew("cms.new.auto.text.supprimer", "Supprimer")}
          aria-label={cmsNew("cms.new.auto.text.supprimer", "Supprimer")}
          className="absolute right-1.5 top-1.5 text-gray-400 transition-colors hover:text-red-500 disabled:opacity-50"
        >
          {isDeleting ? (
            <Loader2 className="h-5 w-5 animate-spin text-red-500" />
          ) : (
            <Trash2 className="h-5 w-5" />
          )}
        </button>
      </div>
    );
  }

  return (
    <label
      className={cn(
        IMAGE_BOX_CLASS,
        "flex cursor-pointer flex-col items-center justify-center gap-2 hover:bg-slate-50 transition-colors",
        isProcessing && "pointer-events-none opacity-50",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
        onChange={handleFileChange}
        className="absolute inset-0 size-full cursor-pointer opacity-0"
        aria-label={cmsNew(
          "cms.new.maintenance.imageClickCta",
          "Cliquez pour ajouter une image",
        )}
      />
      {isUploading ? (
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      ) : (
        <>
          <Upload className="h-8 w-8 text-slate-400" />
          <span className="text-xs text-slate-500">
            {cmsNew(
              "cms.new.maintenance.imageClickCta",
              "Cliquez pour ajouter une image",
            )}
          </span>
        </>
      )}
    </label>
  );
};

export default CmsMaintenanceImageUpload;
